import { useMemo } from "react";
import { Box, IconButton, Tooltip, Typography, Link as MuiLink } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { loadReviewNav, requestReviewNavContinue } from "../../utils/reviewNav";

const BORDER = "rgba(17, 24, 39, 0.08)";

/**
 * Prev / Next sequential-review bar (mounted at the top of ProfileSidebar, so it shows
 * on every profile section). Walks the list-page snapshot captured by CandidatesTable
 * on row click (see utils/reviewNav.ts). Self-gates: renders ONLY when the current
 * profile id is inside the snapshot — profiles opened from search, Slack links, the
 * matching page, etc. never show it. Prev/Next preserve the current section by
 * swapping just the id segment of the path.
 */
export default function ProfileReviewNav({ profileId }: { profileId: string }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Re-read on every navigation (cheap localStorage parse; keeps multi-tab sane).
  const ctx = useMemo(() => loadReviewNav(), [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const idx = ctx ? ctx.ids.indexOf(profileId) : -1;
  if (!ctx || idx === -1) return null;

  const goTo = (targetId: string) =>
    navigate(location.pathname.replace(profileId, targetId));

  const prevId = idx > 0 ? ctx.ids[idx - 1] : null;
  const nextId = idx < ctx.ids.length - 1 ? ctx.ids[idx + 1] : null;
  const globalPos = ctx.offset + idx + 1;
  // More rows exist beyond the snapshot? Then the boundary buttons stay ENABLED and
  // hand off via a continue ticket: back to the source list, which flips to the next
  // server page (its own filters) and auto-opens the first/last row. One click → row 31.
  // Only server-side captures carry `page` (client-side ones hold the full list already).
  const canContinue = ctx.page != null;
  const hasMoreAfter = canContinue && globalPos < ctx.totalCount;
  const hasMoreBefore = canContinue && ctx.offset > 0;
  const listPage = ctx.page ?? 1;

  const handlePrev = () => {
    if (prevId) return goTo(prevId);
    if (!hasMoreBefore) return;
    requestReviewNavContinue({ source: ctx.source, page: Math.max(1, listPage - 1), pos: "last" });
    navigate(ctx.source);
  };
  const handleNext = () => {
    if (nextId) return goTo(nextId);
    if (!hasMoreAfter) return;
    requestReviewNavContinue({ source: ctx.source, page: listPage + 1, pos: "first" });
    navigate(ctx.source);
  };

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        borderBottom: `1px solid ${BORDER}`,
        display: "grid",
        gap: 0.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Tooltip
          title={prevId ? "Previous profile" : hasMoreBefore ? "Previous profile (loads the previous page)" : "Start of the list"}
        >
          <span>
            <IconButton size="small" disabled={!prevId && !hasMoreBefore} onClick={handlePrev}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "rgba(17,24,39,0.65)" }}>
          {globalPos.toLocaleString()} of {ctx.totalCount.toLocaleString()}
        </Typography>
        <Tooltip
          title={nextId ? "Next profile" : hasMoreAfter ? "Next profile (loads the next page)" : "End of the list"}
        >
          <span>
            <IconButton size="small" disabled={!nextId && !hasMoreAfter} onClick={handleNext}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <MuiLink
        component={RouterLink}
        to={ctx.source}
        underline="hover"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          fontSize: 11.5,
          color: "rgba(17,24,39,0.55)",
          justifyContent: "center",
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 13 }} />
        Back to {ctx.label}
      </MuiLink>
    </Box>
  );
}
